import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ShoppingBag, Search } from "lucide-react";

const SORT_OPTIONS = [
  { value: "recent", label: "Plus récents" },
  { value: "price_asc", label: "Prix croissant" },
  { value: "price_desc", label: "Prix décroissant" },
  { value: "name", label: "Nom (A-Z)" },
];

export default function Catalogue() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    api.get("/shop/products").then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => [...new Set(products.map((p) => p.category).filter(Boolean))],
    [products]
  );

  const visibleProducts = useMemo(() => {
    let list = category ? products.filter((p) => p.category === category) : products;
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((p) => p.name.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q));
    list = [...list];
    if (sort === "price_asc") list.sort((a, b) => a.price_cents - b.price_cents);
    else if (sort === "price_desc") list.sort((a, b) => b.price_cents - a.price_cents);
    else if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    // "recent" garde l'ordre déjà trié par created_at (desc) renvoyé par l'API
    return list;
  }, [products, category, query, sort]);

  return (
    <div className="max-w-6xl mx-auto px-5 pt-10 pb-16">
      <div className="mb-8">
        <span className="text-[11px] uppercase tracking-[0.3em] font-display text-[#00F0FF]">Fribue Technologies</span>
        <h1 className="font-display font-black text-4xl md:text-5xl mt-2 tracking-tighter">Boutique</h1>
        <p className="text-white/60 mt-3">Vêtements, goodies et objets de collection La Fribue.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un produit..."
            className="w-full bg-[#14141A] border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm outline-none focus:border-[#00F0FF]"
          />
        </div>
        <select
          value={sort} onChange={(e) => setSort(e.target.value)}
          className="bg-[#14141A] border border-white/10 rounded-full px-4 py-3 text-sm outline-none focus:border-[#00F0FF] shrink-0"
        >
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setCategory(null)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border transition-all ${!category ? "bg-[#FF007F] text-black border-[#FF007F]" : "border-white/10 text-white/70 hover:border-white/30"}`}
          >
            Tout
          </button>
          {categories.map((c) => (
            <button
              key={c} onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border transition-all ${category === c ? "bg-[#FF007F] text-black border-[#FF007F]" : "border-white/10 text-white/70 hover:border-white/30"}`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="card-glass overflow-hidden animate-pulse">
              <div className="aspect-square bg-white/5" />
              <div className="p-4 space-y-2">
                <div className="h-3 bg-white/5 rounded w-3/4" />
                <div className="h-3 bg-white/5 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : visibleProducts.length === 0 ? (
        <div className="text-center py-20 text-white/40 flex flex-col items-center gap-3">
          <ShoppingBag size={32} className="opacity-40" />
          <p>{query || category ? "Aucun produit ne correspond à ta recherche." : "Aucun produit disponible pour le moment."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {visibleProducts.map((p) => (
            <Link key={p.product_id} to={`/produit/${p.product_id}`} className="card-glass overflow-hidden group hover:border-white/20 transition-all">
              <div className="aspect-square overflow-hidden bg-white/5">
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><ShoppingBag size={28} className="text-white/20" /></div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-display font-bold text-sm leading-tight truncate">{p.name}</h3>
                <p className="text-[#00F0FF] font-display font-bold mt-1">{(p.price_cents / 100).toFixed(2)}€</p>
                {p.stock === 0 ? (
                  <p className="text-[10px] text-[#FF0033] uppercase tracking-wider mt-1">Épuisé</p>
                ) : p.stock !== null && p.stock !== undefined && p.stock <= 3 ? (
                  <p className="text-[10px] text-[#FFD700] uppercase tracking-wider mt-1">Plus que {p.stock}</p>
                ) : null}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
